import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, Menu, X, LogOut, ExternalLink } from 'lucide-react';

import { Button } from '../components/ui';
import LanguageSelector from '../components/ui/LanguageSelector';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '../components/ui/Tooltip';
import { Tool } from '../types';

import { getToolGroups, openTool } from './navigation';

interface SidebarProps {
  onSignOut?: () => void;
  isAdmin?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ onSignOut, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Collapse sidebar when not on home page
  const isHomePage = location.pathname === '/';
  const [isCollapsed, setIsCollapsed] = useState(!isHomePage);

  // Update collapsed state when location changes
  useEffect(() => {
    setIsCollapsed(!isHomePage);
  }, [isHomePage]);

  const homeTool: Tool = {
    id: 'home',
    title: t('navigation.home'),
    icon: Shield,
    path: '/',
  };
  const toolGroups = getToolGroups(t, isAdmin);

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleToolClick = (tool: Tool) => {
    openTool(tool, navigate);
    setIsMobileMenuOpen(false);
  };

  const renderMobileTool = (tool: Tool) => {
    const IconComponent = tool.icon;
    return (
      <button
        key={tool.id}
        onClick={() => handleToolClick(tool)}
        className={`w-full flex items-center px-3 py-2 text-sm text-left rounded-md transition-colors ${
          !tool.external && isActivePath(tool.path)
            ? 'bg-blue-100 text-blue-900'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <IconComponent className="w-4 h-4 mr-3" />
        {tool.title}
        {tool.external && (
          <ExternalLink className="w-3 h-3 ml-auto text-gray-400" />
        )}
      </button>
    );
  };

  const renderDesktopTool = (tool: Tool) => {
    const IconComponent = tool.icon;
    return (
      <Tooltip key={tool.id}>
        <TooltipTrigger asChild>
          <button
            onClick={() => openTool(tool, navigate)}
            className={`w-full flex items-center text-left rounded-md transition-colors ${
              isCollapsed ? 'justify-center p-3' : 'px-3 py-2 text-sm'
            } ${
              !tool.external && isActivePath(tool.path)
                ? 'bg-blue-100 text-blue-900'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <IconComponent className="w-4 h-4" />
            {!isCollapsed && <span className="ml-3">{tool.title}</span>}
            {!isCollapsed && tool.external && (
              <ExternalLink className="w-3 h-3 ml-auto text-gray-400" />
            )}
          </button>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="ml-2">
            {tool.title}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => handleNavigation('/')}
          className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
        >
          <img src="/um-logo.webp" alt="UM" className="h-6 w-auto" />
          <div>
            <h1 className="text-base font-semibold text-gray-900">
              {t('sidebar.umTools')}
            </h1>
          </div>
        </button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2"
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </Button>
      </div>

      {/* Mobile Menu Overlay - Full Width */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handleNavigation('/')}
                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
              >
                <img src="/um-logo.webp" alt="UM" className="h-6 w-auto" />
                <div>
                  <h1 className="text-base font-semibold text-gray-900">
                    {t('sidebar.umTools')}
                  </h1>
                </div>
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <nav className="py-4 overflow-y-auto max-h-[calc(100vh-180px)]">
            <div className="px-3 space-y-1">
              {renderMobileTool(homeTool)}
              {toolGroups.map((group) => (
                <div key={group.id} className="pt-3">
                  <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.title}
                  </p>
                  <div className="space-y-1">
                    {group.tools.map(renderMobileTool)}
                  </div>
                </div>
              ))}
            </div>
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
            <div className="mb-3">
              <LanguageSelector dropdownDirection="above" />
            </div>
            <Button
              variant="outline"
              onClick={onSignOut}
              className="w-full gap-2"
              size="sm"
            >
              <LogOut className="w-4 h-4" />
              {t('auth.signout')}
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div
        className={`hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 bg-white border-r border-gray-200 flex-col z-40 transition-all duration-300 ${
          isCollapsed ? 'lg:w-20' : 'lg:w-64'
        }`}
      >
        <div
          className={`border-b border-gray-200 ${isCollapsed ? 'p-3' : 'p-6'}`}
        >
          <button
            onClick={() => handleNavigation('/')}
            className="flex items-center hover:opacity-80 transition-opacity w-full text-left"
            title={isCollapsed ? t('sidebar.umTools') : ''}
          >
            <img src="/um-logo.webp" alt="UM" className="h-8 w-auto" />
            {!isCollapsed && (
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">
                  {t('sidebar.umTools')}
                </h1>
              </div>
            )}
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <div className={`space-y-1 ${isCollapsed ? 'px-2' : 'px-3'}`}>
            {renderDesktopTool(homeTool)}
            {toolGroups.map((group) =>
              isCollapsed ? (
                <div key={group.id} className="pt-2 space-y-1">
                  <div className="mx-2 mb-2 border-t border-gray-200" />
                  {group.tools.map(renderDesktopTool)}
                </div>
              ) : (
                <div key={group.id} className="pt-3">
                  <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group.title}
                  </p>
                  <div className="space-y-1">
                    {group.tools.map(renderDesktopTool)}
                  </div>
                </div>
              )
            )}
          </div>
        </nav>

        <div
          className={`border-t border-gray-200 ${isCollapsed ? 'p-2' : 'p-4'}`}
        >
          <div className={isCollapsed ? 'mb-2' : 'mb-3'}>
            <LanguageSelector
              dropdownDirection="above"
              isCollapsed={isCollapsed}
            />
          </div>
          <Button
            variant="outline"
            onClick={onSignOut}
            className={
              isCollapsed
                ? 'p-0 h-auto w-full flex justify-center'
                : 'w-full gap-2'
            }
            size={isCollapsed ? 'sm' : 'sm'}
            title={isCollapsed ? t('auth.signout') : ''}
          >
            <LogOut className="w-4 h-4" />
            {!isCollapsed && t('auth.signout')}
          </Button>
        </div>
      </div>
    </>
  );
};
